import { BookOpen, ExternalLink } from "lucide-react";
import { getReferences } from "@/lib/blog-references";

/**
 * References block appended to every clinical library article.
 * Renders a numbered list of real, peer-reviewed citations with
 * author, journal, year and a PubMed / DOI link. Also emits a
 * short "Key studies cited inline" summary line naming the
 * original researchers and journals for at-a-glance credit.
 */
export function References({ slug }: { slug: string }) {
  const refs = getReferences(slug);
  if (refs.length === 0) return null;

  const inlineCredit = refs
    .map((r) => {
      const first = r.authors.split(",")[0]?.trim() ?? r.authors;
      return `${first}${r.authors.includes(",") ? " et al." : ""} (${r.year}, ${r.journal})`;
    })
    .join("; ");

  return (
    <section
      aria-labelledby="references-heading"
      className="mt-16 rounded-3xl border border-border bg-muted/30 p-6 md:p-8"
    >
      <div className="flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-brand" />
        <h2
          id="references-heading"
          className="font-display text-lg font-semibold text-brand-deep"
        >
          References
        </h2>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Every in-text claim in this article is credited to the original
        researchers and peer-reviewed journal below. Key studies cited:{" "}
        {inlineCredit}.
      </p>
      <ol className="mt-6 space-y-5 text-xs text-muted-foreground md:text-sm">
        {refs.map((r, i) => {
          const isDoi = /doi\.org/i.test(r.url);
          const linkLabel = isDoi ? "DOI" : "PubMed";
          return (
            <li
              key={r.id}
              id={`ref-${i + 1}`}
              className="rounded-2xl border border-border/60 bg-background/60 p-4 leading-relaxed"
            >
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-brand-deep">
                  [{i + 1}]
                </span>
                <p className="font-medium text-brand-deep">{r.title}</p>
              </div>
              <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[110px_1fr]">
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                  Author(s)
                </dt>
                <dd>{r.authors}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                  Year
                </dt>
                <dd>{r.year}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                  Journal
                </dt>
                <dd className="italic">{r.journal}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                  Citation
                </dt>
                <dd className="text-muted-foreground">{r.citation}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
                  {linkLabel}
                </dt>
                <dd>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                  >
                    {r.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </dd>
              </dl>
            </li>
          );
        })}
      </ol>
      <p className="mt-6 text-[11px] text-muted-foreground">
        Citations follow AMA format. TruWe does not own or endorse the linked
        journals; all rights and credit remain with the original authors and
        their respective publishers.
      </p>
    </section>
  );
}
